// THE CONSENT FORM'S OWN PAGE: ITS WORDS, IN BOTH LANGUAGES (TW-0249, redrafted 2026-10-16). TermsAndConditions.tsx draws
// them at /terms-and-conditions on every shop's subdomain, the page a client opens from the terms box on the form, and its
// header comment is the one to read before a word here changes: who the two parties are and why, why the marketing consent
// is intent and not boilerplate, and the ads sentence removed on 2026-09-08 for the carriers.
//
// THEY READ AS A LEGAL DOCUMENT, because they are one: this is what a client agrees to when they tick "I accept the terms
// and conditions" (docs/RULES-IN-FULL.md → How we work here → A legal document reads as one). Chris, 2026-09-29, of the
// deposits terms' first cut, whose title was "The deposits deal, in plain language": "seems cheeky and not legal is my first
// immediate feedback"; and of the waivers.tattoo terms, whose register this page shared: "i haven't reviewed the waivers
// terms in a while and i'm not a lawyer". So, as apps/marketing/lib/terms.ts does: the document's own name for a title, the
// parties and the defined terms first, numbered sections under conventional headings, lettered clauses, a heading of its own
// on each clause where a section holds more than one subject, full words. Plain sentences still. Its name is the one the
// terms box and the address already give it: "Terms and Conditions".
//
// EVERY STATEMENT OF THE PAGE AS IT STOOD IS STILL HERE, AND NONE WAS ADDED, BUT ONE CORRECTED: docs/legal/
// consent-page-clause-map-2026-10-16.md carries every sentence to the clause that makes it now, and
// TermsAndConditions.test.tsx holds each one. The one correction is 3(d): the page said it ran "no analytics", and since
// TW-0131 (2026-09-08) the form counts its own page loads and errors, this page's included (beacon.ts routeOf), so it now
// says what the privacy policy has said since 2026-09-23 ("counts its own page loads and errors, with no name, no cookie and
// no identifier"). The effective date stands (CONSENT_PAGE_EFFECTIVE) with a "clarified" date beside it.
//
// AND THE SAME DAY, WHAT THE PRODUCT HAD OUTGROWN SAYS WHAT THE CODE DOES (TW-0249, the fourth slice). Chris, 2026-10-16:
// "But in general or always if we build product features or code we can always update those documents, rather than let
// those documents limit us. I thought we had noted that." · "Our privacy and terms follow the business and product, not the
// other way around." So the second party is the company, as the terms and the privacy policy name it (config.tsx
// LEGAL_ENTITY, held to the root entity.json by the test, never typed here), with "waivers.tattoo" its short name; 3(a) names
// everything the form can collect, held by the test to Form.tsx's own fields; 3(c) names the AI assistants among those who
// can see a Record; 3(d) says what the count is, a random key per page load, in the privacy policy's words; and 3(e) says
// where the health answers are kept, the Shop's export and the client's own copy included. The clause map's last section
// has each, old beside new. A correction of a statement to what the product already did changes nothing a client agreed
// to, so the date line stands and October 16's "clarified" covers it.
//
// AND SECTION 5 SAYS WHAT THE FORM DOES FOR A MINOR (TW-0327, the privacy policy's sixth slice, 2026-10-17). It said "This
// form is for adults.", untrue the way the privacy policy's old 3(f) was: the form takes a parent's or guardian's consent
// where the law where the Shop operates and the Shop's own switch for that work allow it (App.tsx tattoo_minors and
// pierce_minors, minors.ts minorRefusal, the API's state_forms.minor_refusal), the parent or guardian signing on the form,
// and no minor's signing for a cosmetic tattoo in any state (Form.tsx; the contract's `cosmetic` entry). So the section says
// both, in each kind of shop's words (termsWords' minorWork and cosmetic), and the privacy policy's 3(f) says the same. A
// correction of a statement to what the form already did since 2026-09-08 (TW-0128), so the effective date stands and the
// "clarified" part of the date line names the day beside October 16's. The clause map's last section has it, old beside new.
//
// AND 3(f), WHAT THE BROWSER KEEPS (TW-0500, 2026-10-26). Since that day the form keeps what a client has typed and ticked in the
// tab's own storage until they sign, so a reload loses nothing (draft.ts): never a photo, a signature, the ID's number or the two
// optional boxes, put back for 30 minutes after the last keystroke. 3(e) said the health answers were kept in the Record and the
// Signed Copy and nowhere else, which the draft made untrue, so it names the browser beside them, and 3(f) says what the browser
// keeps. A statement of what the form does, built first and written after (Chris's rule, above); nothing a client agreed to
// moves, and October 26 is already in the "clarified" part. The clause map's last section has both, old beside new.
//
// AND AT A SHOP IN HIPAA MODE, NONE OF IT IS A HEALTH ANSWER (TW-0501, 2026-10-27). Since TW-0501 a shop in HIPAA mode's draft
// keeps the typed boxes and the ticks and no health answer, no answer to the shop's own questions and nothing of the physician
// (draft.ts neverDraftedFor, Form.tsx), which made 3(e) and 3(f) untrue there, so 3(e) says "unless the Shop is in HIPAA mode"
// and 3(f) ends on what the browser keeps none of at such a Shop. One text for every shop, as since TW-0249: the page draws on
// the static file, which never says whether a shop is in HIPAA mode, before `/shop` lands, so the sentence names the mode and
// the page does not vary by it ("HIPAA mode" is the privacy policy's name for the plan, its 7(b)). Nothing a client agreed to
// moves, so the effective date stands and October 27 joins the "clarified" part. The clause map's last section has both.
//
// AND 3(d) AT A SHOP IN HIPAA MODE, THE SAME DAY (TW-0501). Since 2026-10-08 a shop in HIPAA mode's street box asks for no
// address suggestions (address.ts shouldSuggest on `/shop`'s `hipaa: true`, Form.tsx), and the API's address door answers such
// a shop empty before Google is asked (services/waiver-api/consolidated_api/address.py), the one guard for the second or two
// before `/shop` lands. So nothing typed there reaches Google, which made 3(d)'s one exception untrue at such a Shop, and it
// says "unless the Shop is in HIPAA mode", the way 3(e) does, one text for every shop. Nothing a client agreed to moves (less
// goes to Google at such a Shop, never more), and October 27 is already in the "clarified" part. The clause map's last
// section has it, old beside new.
//
// AND 3(a) NAMES THE PARENT'S OR GUARDIAN'S PHONE (TW-0526, the client-form side, 2026-10-29). Since that day a minor's route asks
// the parent's or guardian's phone number in every state, under their name and relationship, and Utah's rule requires it
// (R392-701-16(2)(c)(iv); stateForms.ts stateFieldsFor, Form.tsx). 3(a) named the parent's name, relationship and signature, so it
// names the phone between them; the time a client last ate (Oklahoma) and their sex as on the ID (Mississippi) are answers to this
// form's questions, which 3(a) names already. A statement of what the form collects, built first and written after (Chris's rule,
// above): nothing a client agreed to moves, so the effective date stands and October 29 joins the "clarified" part. The clause
// map's last section has it, old beside new.
//
// ⚠ THE CONSENT SENTENCES A CLIENT TICKS ARE NOT HERE AND ARE NOT THIS PAGE: they live in the root
// consent-sentences-es.json and, in English, in service.ts and i18n.ts, and they are law-bound by state. This page frames
// them and never restates them; TermsAndConditions.test.tsx holds their bytes.
//
// ⚠ SECTION 4, HEARING FROM THE SHOP, IS NAMED BY THE CARRIERS' FILINGS: every shop's campaign says the consent page's
// "section 'Hearing from the shop' states that saying yes is not a condition of service, that texts are sent through
// waivers.tattoo on the shop's behalf, that frequency depends on the shop and message and data rates may apply, and that
// STOP ends the messages and HELP gets help" (services/waiver-api/consolidated_api/texting.py campaign_for;
// infrastructure/twilio/register-campaign.py), and infrastructure/twilio/preflight.py reads this page for REQUIRED_CONSENT.
// Keep each of those five things in it, and never a sentence that reads as a yes to "are mobile numbers shared with third
// parties for marketing?".
//
// The Spanish mirrors the English clause for clause. It is a Fable session's and then an Opus session's translation, awaiting
// a native-speaker read as well as the legal one, like every Spanish line of the form.
//
// Plain strings and no markup, so a test can read them: a paragraph is a list of pieces, a string is text and `{ text, href
// }` a link, which the page opens in a new tab. A paragraph carries the letter of its clause when its section holds more
// than one.
import { LEGAL_ENTITY, LEGAL_ENTITY_PHRASE, LEGAL_ENTITY_PHRASE_ES, SITE_PRIVACY_URL } from "./config";
import type { Lang } from "./i18n";
import type { ServiceType } from "./service";

/** The day the page took effect, which every client who signed since has agreed under. */
export const CONSENT_PAGE_EFFECTIVE = "2026-09-08";

/** The day of the last clarification on the line: the redraft's was 2026-10-16, and section 5's correction (TW-0327) is this
 *  one, carried in the same "clarified" part the way the terms carry two days in one ("clarified September 11 and October 5,
 *  2026"); and 2026-10-19 is 3(d)'s Google Maps notice (TW-0336, the seventh slice's rest), the third day in the same part; and
 *  2026-10-26 is 3(a) naming which identification it is and its number (TW-0488), the fifth, and the same day 3(e) and the new
 *  3(f), what the browser keeps until the signing (TW-0500); and 2026-10-27 is 3(d), 3(e) and 3(f) at a Shop in HIPAA mode
 *  (TW-0501), the sixth. If it ships on another day, this and both date lines move together (TermsAndConditions.test.tsx ties
 *  them). And 2026-10-29 is 3(a) naming the parent's or guardian's phone number (TW-0526, the client-form side), the seventh. */
export const CONSENT_PAGE_CLARIFIED = "2026-10-29";

/** The page's date line, in each language: the effective date, then the clarifications. */
export const CONSENT_PAGE_DATE_LINE: Record<Lang, string> = {
  en: "Effective September 8, 2026 · clarified October 16, 17, 19, 20, 26, 27 and 29, 2026",
  es: "Vigente desde el 8 de septiembre de 2026 · aclarado el 16, el 17, el 19, el 20, el 26, el 27 y el 29 de octubre de 2026",
};

/** Where a client writes to waivers.tattoo, as the page has always said it (plain text, never a link). */
export const OPERATOR_EMAIL = "hello@waivers.tattoo";

/** The document's own name, with the shop's: the name the terms box ("I accept the terms and conditions") and the address
 *  (/terms-and-conditions) already give it. */
export const consentPageTitle = (shop: string, lang: Lang): string =>
  lang === "es"
    ? `Términos y condiciones del formulario de consentimiento de ${shop}`
    : `${shop} Consent Form Terms and Conditions`;

export type ConsentPiece = string | { text: string; href: string };
export type ConsentParagraph = { clause?: string; pieces: ConsentPiece[] };
export type ConsentSection = { title: string; paragraphs: ConsentParagraph[] };
export type ConsentPage = { title: string; dateLine: string; intro: ConsentParagraph[]; sections: ConsentSection[] };

/** What a paragraph says, as text: its pieces joined, a link by its words. */
export const piecesText = (pieces: readonly ConsentPiece[]): string =>
  pieces.map((piece) => (typeof piece === "string" ? piece : piece.text)).join("");

/** A paragraph as a reader reads it: its clause's letter in parentheses, then its words. */
export const paragraphText = (paragraph: ConsentParagraph): string =>
  `${paragraph.clause ? `(${paragraph.clause}) ` : ""}${piecesText(paragraph.pieces)}`;

/** A section's words, its paragraphs in order, one to a line. */
export const sectionText = (section: ConsentSection): string => section.paragraphs.map(paragraphText).join("\n");

/** The words that name what the shop does, in one place for each language (TW-0279). A tattoo shop's, a piercing
 *  studio's and a `both` shop's are word for word the ones this page has had since 2026-09-02; a shop whose services
 *  include cosmetic tattooing reads its own. A cosmetic tattoo IS tattooing, so a shop that also tattoos keeps the
 *  tattoo words, and a cosmetic studio reads "cosmetically tattooed". The jewelry clause rides only where a piercing is
 *  offered: before this it rode with every type but tattoo, which a cosmetic studio would have read. `jewelryKept` (TW-0249,
 *  the fourth slice) is the jewelry a piercing's record keeps (the form's jewelryMaterial and jewelryNotes), named in 3(a)
 *  where a piercing is offered and nowhere else. `minorWork` and `cosmetic` (TW-0327) are section 5's: the work a parent's
 *  or guardian's consent reaches on this form ("" where none does: a cosmetic studio's form takes no minor at all, and a
 *  cosmetic tattoo is never that work), and whether a cosmetic tattoo can be asked for on the form, so that the section
 *  says no minor signs for one (every shop whose services name tattooing, its page being a tattoo shop's word for word). */
export type TermsWords = {
  work: string;
  mark: string;
  record: string;
  permanence: string;
  jewelry: string;
  jewelryKept: string;
  minorWork: string;
  cosmetic: boolean;
};
export type TermsWordsEs = {
  work: string;
  workVerb: string;
  record: string;
  permanence: string;
  jewelry: string;
  jewelryKept: string;
  minorWork: string;
  cosmetic: boolean;
};

export function termsWords(services: ServiceType[]): TermsWords {
  const tattoo = services.includes("tattoo") ? "tattoo" : services.includes("cosmetic") ? "cosmetic" : null;
  const piercing = services.includes("piercing");
  const jewelry = piercing ? ", jewelry the body moves or rejects" : "";
  if (tattoo && piercing) {
    return {
      work: tattoo === "tattoo" ? "tattooed or pierced" : "cosmetically tattooed or pierced",
      mark: tattoo === "tattoo" ? "a tattoo or a piercing" : "a cosmetic tattoo or a piercing",
      record: "the artist or piercer, the Work and its placement",
      permanence: "leaves a permanent mark",
      jewelry,
      jewelryKept: ", and the jewelry for a piercing",
      minorWork: tattoo === "tattoo" ? "tattooed or pierced" : "pierced",
      cosmetic: true,
    };
  }
  if (piercing) {
    return {
      work: "pierced",
      mark: "a piercing",
      record: "the piercer, the piercing and its placement",
      permanence: "leaves a mark that may never close",
      jewelry,
      jewelryKept: ", and the jewelry",
      minorWork: "pierced",
      cosmetic: false,
    };
  }
  if (tattoo === "cosmetic") {
    return {
      work: "cosmetically tattooed",
      mark: "a cosmetic tattoo",
      record: "the artist, the cosmetic tattoo and its placement",
      permanence: "is permanent",
      jewelry,
      jewelryKept: "",
      minorWork: "",
      cosmetic: true,
    };
  }
  return {
    work: "tattooed",
    mark: "a tattoo",
    record: "the artist, the tattoo and its placement",
    permanence: "is permanent",
    jewelry,
    jewelryKept: "",
    minorWork: "tattooed",
    cosmetic: true,
  };
}

// awaiting a native-speaker read and a legal one, like the rest of this page's Spanish
export function termsWordsEs(services: ServiceType[]): TermsWordsEs {
  const tattoo = services.includes("tattoo") ? "tattoo" : services.includes("cosmetic") ? "cosmetic" : null;
  const piercing = services.includes("piercing");
  const jewelry = piercing ? ", joyería que el cuerpo desplaza o rechaza" : "";
  if (tattoo && piercing) {
    const noun = tattoo === "tattoo" ? "un tatuaje" : "un tatuaje cosmético";
    return {
      work: `${noun} o una perforación`,
      workVerb: `hacerte ${noun} o una perforación`,
      record: "el artista o perforador, el Trabajo y su ubicación",
      permanence: "deja una marca permanente",
      jewelry,
      jewelryKept: ", y la joyería de una perforación",
      minorWork: tattoo === "tattoo" ? "un tatuaje o una perforación" : "una perforación",
      cosmetic: true,
    };
  }
  if (piercing) {
    return {
      work: "una perforación",
      workVerb: "hacerte una perforación",
      record: "el perforador, la perforación y su ubicación",
      permanence: "deja una marca que puede no cerrarse nunca",
      jewelry,
      jewelryKept: ", y la joyería",
      minorWork: "una perforación",
      cosmetic: false,
    };
  }
  if (tattoo === "cosmetic") {
    return {
      work: "un tatuaje cosmético",
      workVerb: "hacerte un tatuaje cosmético",
      record: "el artista, el tatuaje cosmético y su ubicación",
      permanence: "es permanente",
      jewelry,
      jewelryKept: "",
      minorWork: "",
      cosmetic: true,
    };
  }
  return {
    work: "un tatuaje",
    workVerb: "hacerte un tatuaje",
    record: "el artista, el tatuaje y su ubicación",
    permanence: "es permanente",
    jewelry,
    jewelryKept: "",
    minorWork: "un tatuaje",
    cosmetic: true,
  };
}

const p = (...pieces: ConsentPiece[]): ConsentParagraph => ({ pieces });
const clause = (letter: string, ...pieces: ConsentPiece[]): ConsentParagraph => ({ clause: letter, pieces });
const privacyLink = (): ConsentPiece => ({ text: SITE_PRIVACY_URL, href: SITE_PRIVACY_URL });
/** Google's two documents, at the addresses its Maps Platform terms give them (3.2.2(a)(i)): 3(d), in both languages. */
export const GOOGLE_MAPS_TERMS_URL = "https://maps.google.com/help/terms_maps/";
export const GOOGLE_PRIVACY_URL = "https://policies.google.com/privacy";

/** The page in the language it is read in, for the shop's name and what the shop does (its services). */
export function consentPage(lang: Lang, shop: string, services: ServiceType[]): ConsentPage {
  return lang === "es" ? spanish(shop, termsWordsEs(services)) : english(shop, termsWords(services));
}

function english(shop: string, words: TermsWords): ConsentPage {
  const { work, mark, record, permanence, jewelry, jewelryKept, minorWork, cosmetic } = words;
  return {
    title: consentPageTitle(shop, "en"),
    dateLine: CONSENT_PAGE_DATE_LINE.en,
    intro: [
      p(
        `These ${consentPageTitle(shop, "en")} (the “Terms”) are an agreement between you, the person signing this form (“you” or ` +
          `“your”), and 2 parties: ${shop} (the “Shop”) and ${LEGAL_ENTITY}, ${LEGAL_ENTITY_PHRASE}, which operates ` +
          "waivers.tattoo and runs this form for the Shop (“waivers.tattoo”)."
      ),
      p(
        `This form is your consent to be ${work} at the Shop. In these Terms, the “Work” is ${mark} that the Shop does for you, ` +
          "your “Record” is everything you give on this form together with the waiver you sign, and the “Signed Copy” is " +
          "the PDF file of the waiver as you signed it. The Shop does the Work and keeps the Record."
      ),
      p("You accept these Terms when you tick the box on this form that accepts the terms and conditions."),
    ],
    sections: [
      {
        title: "Your Representations",
        paragraphs: [
          clause(
            "a",
            "You represent to the Shop that everything you enter on this form is true and complete, including who you are, how " +
              "to reach you, your age and your answers about your health. The Shop relies on those answers to keep you safe. If " +
              "something is missing or wrong, the risk is yours, and the Shop is not responsible for what follows from it."
          ),
          clause(
            "b",
            "You also represent that you are not under the influence of alcohol or drugs, that you have told the Shop about any " +
              "condition that matters (such as pregnancy, blood thinners, a skin condition, a communicable disease or an allergy " +
              `you know of), and that you understand that ${mark} ${permanence}. Nobody at the Shop has promised you that the ` +
              "Work can be changed or removed later."
          ),
        ],
      },
      {
        title: "Assumption of Risk and Release",
        paragraphs: [
          clause(
            "a",
            `Assumption of risk. You accept the risks of being ${work}: infection, allergic reaction, scarring${jewelry}, and a ` +
              "result that differs from what you pictured."
          ),
          clause(
            "b",
            "Release. You release the Shop, its artists and its staff from claims for injury, loss or damage arising from the " +
              "Work, except where the law does not allow a release."
          ),
          clause(
            "c",
            "Aftercare. You will follow the aftercare instructions the Shop gives you, and you understand that not following " +
              "them is the most common cause of a bad outcome."
          ),
          clause("d", "Indemnification. You will indemnify the Shop against claims that arise from your own breach of these Terms."),
          clause(
            "e",
            "Refusal of service. The Shop may refuse or stop the Work at any time, for any reason it judges right, and sets the " +
              "rules of conduct inside its space."
          ),
        ],
      },
      {
        title: "Your Information",
        // NO ADS SENTENCE ON THIS PAGE (2026-10-20): what a shop may do with a client's email and name afterwards, the ads product's
        // audience (TW-0363, docs/ADS.md), is the privacy policy's 3(e) and the terms' 3(d), linked from this form at the point of
        // consent, never a clause here: every shop's texting campaign files THIS page for the carriers' reviewers to open, and a
        // sentence about Facebook and Instagram on it is the shape of the 2026-09-08 refusals (TermsAndConditions.test.tsx holds
        // the ad words off it). That is the carriers' concern, not a promise of ours; 3(d) says "at the signing" so the page stays
        // true beside the privacy policy.
        paragraphs: [
          // everything Form.tsx can collect, held to its own fields by TermsAndConditions.test.tsx: the every-form list, then
          // what the Shop's switches or the rules where it operates add, then what the artist adds after the signing
          clause(
            "a",
            "Collection. This form collects your name, address, phone number, email address, age and date of birth; a " +
              "photograph of your government identification, taken with your phone or, where the Shop allows it, chosen from " +
              "your saved photographs; which identification it is, when you say; your answers to this form’s questions, about " +
              "your health among them, which depend on the " +
              `Work and on the law of the place where the Shop operates; ${record}${jewelryKept}; every box you tick; the ` +
              "language you sign in; and your signature, with the date and time. Where the Shop or that law asks for them, this " +
              "form also collects an emergency contact, your physician, the price of the Work, how you are paying and the number " +
              "on your identification; for a " +
              "minor, the parent’s or guardian’s name, relationship, phone number and signature, the other parent’s name and signature or a " +
              "photograph of that parent’s written permission, a photograph of the minor’s identification, and photographs of " +
              "the papers that law asks for, such as a birth certificate or guardianship papers; photographs of a doctor’s " +
              "prescription or written instructions; photographs of the needle and ink lot numbers; and your answers to the " +
              "Shop’s own clauses, to its own questions and to its notice of a fee for paying by card. After you sign, where the " +
              "Shop or that law asks for them, the artist adds the artist’s own signature and the lot numbers and expiration " +
              "dates of the supplies used."
          ),
          clause(
            "b",
            "Keeping the Record. Your Record becomes the Signed Copy, which goes to the Shop and stays on file for the Shop, " +
              "because the law where the Shop operates requires the Shop to keep it, in most states for at least 2 years."
          ),
          // who can see a Record, the privacy policy's 8(a) in this page's words: the AI assistants among them, and a shop in
          // HIPAA mode keeps them out (the privacy policy's 7(b)), which "nobody but" leaves true
          clause(
            "c",
            "Storage. waivers.tattoo stores the Record for the Shop, on servers in the United States, encrypted, and shows it to " +
              "nobody but the Shop, and the people who run waivers.tattoo and the artificial intelligence assistants they use, " +
              "to help the Shop and to keep waivers.tattoo working."
          ),
          // the count, in the privacy policy's 3(b) words (beacon.ts: one fresh random key per page load, kept by nothing); and the
          // street address to Google at every Shop but one in HIPAA mode, whose street box asks nothing (address.ts shouldSuggest)
          // and whose API door answers empty before Google is asked (address.py; TW-0501, 2026-10-27)
          clause(
            "d",
            "What leaves this form. This page runs no advertising pixel and no third-party scripts, and it counts its own page " +
              "loads, the steps of a signing and its errors, with no name and no cookie: each page load carries a random key " +
              "made for that page load alone, which your browser does not keep once the page is closed. What you type goes to " +
              "the Shop and nowhere else at the signing, with one exception: unless the Shop is in HIPAA mode, as you type your " +
              "street address, what you have typed so far goes to Google to suggest the full address, so that you can tap it " +
              "instead of typing the rest. Nothing else on this " +
              // the notice Google's Maps Platform terms ask of every application built on them, 3.2.2(a)(i): the suggestions are
              // Places API (New) content (services/waiver-api/consolidated_api/address.py), and their use is subject to Google's
              // own two documents (TW-0336, the seventh slice's rest, 2026-10-18; the terms' 1(d) says it for a shop)
              "form goes to Google. The suggestions are Google Maps content, and their use is subject to the then-current " +
              "versions of the ",
            { text: "Google Maps End User Additional Terms of Service", href: GOOGLE_MAPS_TERMS_URL },
            " and the ",
            { text: "Google Privacy Policy", href: GOOGLE_PRIVACY_URL },
            "."
          ),
          // where the health answers are kept: the Record and the Signed Copy, the Shop's export when it asks for them
          // (shop_export.py health=1), the client's own copy where the state's rule or the Shop provides it (client_copy.py), and
          // the browser until the signing at every shop but one in HIPAA mode (3(f); TW-0501, draft.ts neverDraftedFor)
          clause(
            "e",
            "Health answers and identification. Your health answers and the photograph of your identification are the most " +
              "sensitive information on this form. They are kept in your Record and in the Signed Copy, which goes to the Shop " +
              "and, where the law of the place where the Shop operates or the Shop provides for it, to you, and the Shop may " +
              "include your health answers in an export of its own records; unless the Shop is in HIPAA mode, your browser also " +
              "keeps your health answers until you sign, as Section 3(f) describes; and they are kept nowhere else: never in the " +
              "words of a message, never in any marketing, and never shared beyond the Shop."
          ),
          // TW-0500 (2026-10-26) — what the browser keeps while the client fills the form in, so a reload loses nothing: the tab's
          // own storage, what was typed and ticked, never a photo, a signature, the ID's number or the two optional boxes, put back
          // for 30 minutes after the last keystroke, gone at the signing and at "Start over" (draft.ts; TermsAndConditions.test.tsx
          // holds each of these words to that file); and since TW-0501 (2026-10-27), at a shop in HIPAA mode, none of the health
          // answers, the shop's own questions or the physician (draft.ts neverDraftedFor, Form.tsx), its last sentence
          clause(
            "f",
            "What your browser keeps. Until you sign, your browser keeps the answers you have typed and the boxes you have " +
              "ticked on this form, in its own storage for this tab, so that reloading the page does not lose them, and the " +
              "form puts them back for up to 30 minutes after you last type. It keeps no photograph, no signature, no " +
              "identification number and neither of the 2 boxes about hearing from the Shop; nothing it keeps leaves your " +
              "device; and signing, or pressing “Start over”, removes it. Where the Shop is in HIPAA mode, your browser keeps " +
              "none of your health answers, your answers to the Shop’s own questions or your physician’s details."
          ),
        ],
      },
      {
        title: "Hearing from the Shop",
        paragraphs: [
          clause(
            "a",
            "By signing, you allow the Shop to contact you about today at the phone number and email address you give: a copy " +
              "of your waiver, your aftercare instructions and anything about this appointment."
          ),
          clause(
            "b",
            "Consent to hear from the Shop after today is given in 2 separate boxes on this form, and both start unticked, " +
              "because they are 2 different things to agree to. The first is about your own healing: the Shop may text or email " +
              "you about your aftercare and how it is coming along. The second is about the Shop’s news, offers and events. You " +
              "may tick either, both or neither."
          ),
          clause(
            "c",
            "Your mobile number and these consents are never shared with anyone for marketing or promotional purposes. If you " +
              `leave both boxes unticked, none of that happens: saying yes is not a condition of getting ${work}, you can say no ` +
              "and still sign, and you can stop at any time. Your health answers and the photograph of your identification are " +
              "never part of any of this. Neither the Shop nor waivers.tattoo sells your information."
          ),
          clause(
            "d",
            "Texts are sent through waivers.tattoo on the Shop’s behalf; how often depends on the Shop, and message and data " +
              "rates may apply. To stop the messages, reply STOP to a text, use the link in an email or tell the Shop; reply " +
              `HELP to a text for help. To reach waivers.tattoo directly, write to ${OPERATOR_EMAIL}.`
          ),
        ],
      },
      {
        title: "Age",
        paragraphs: [
          // what the form does for a minor (TW-0327; the privacy policy's 3(f) says the same): the Shop's switch for each kind of
          // work and the law where it operates (App.tsx, minors.ts minorRefusal, the API's state_forms.minor_refusal), the
          // parent's or guardian's consent and signature on the form (Form.tsx), and no minor's signing for a cosmetic tattoo
          // in any state (Form.tsx, the contract's `cosmetic` entry). TermsAndConditions.test.tsx holds every kind of shop to it
          p(
            [
              minorWork
                ? `Where the law of the place where the Shop operates allows a minor to be ${minorWork} with a parent’s or ` +
                  "guardian’s consent, and the Shop allows it, the parent or guardian gives that consent on this form and signs " +
                  "for the minor, and the Shop is responsible for that consent and its record."
                : "",
              cosmetic ? "This form takes no minor’s signing for a cosmetic tattoo, even with a parent’s or guardian’s consent." : "",
            ]
              .filter(Boolean)
              .join(" ")
          ),
        ],
      },
      {
        title: "General",
        paragraphs: [
          clause(
            "a",
            "Governing law. Your agreement with the Shop is governed by the law of the state where the Shop operates, and any " +
              "dispute about the Work must be brought in the courts of that state."
          ),
          clause("b", "Questions. Your questions about your Record go to the Shop first, because the Record is the Shop’s."),
          clause(
            "c",
            "Privacy. The waivers.tattoo Privacy Policy, which describes how waivers.tattoo handles what shops collect, is at ",
            privacyLink(),
            "."
          ),
          clause(
            "d",
            "Severability. If a provision of these Terms is held to be unenforceable, the remaining provisions remain in effect."
          ),
          clause("e", "Headings. The headings in these Terms are for convenience only and do not affect their meaning."),
        ],
      },
    ],
  };
}

// The Spanish, clause for clause the English above; a legal read and a native-speaker read are both still owed, and the
// source says so here rather than on the page.
function spanish(shop: string, words: TermsWordsEs): ConsentPage {
  const { work, workVerb, permanence, record, jewelry, jewelryKept, minorWork, cosmetic } = words;
  return {
    title: consentPageTitle(shop, "es"),
    dateLine: CONSENT_PAGE_DATE_LINE.es,
    intro: [
      p(
        `Estos ${consentPageTitle(shop, "es")} (los “Términos”) son un acuerdo entre tú, la persona que firma este formulario ` +
          `(“tú”), y 2 partes: ${shop} (el “Estudio”) y ${LEGAL_ENTITY}, ${LEGAL_ENTITY_PHRASE_ES}, que opera waivers.tattoo ` +
          "y gestiona este formulario para el Estudio (“waivers.tattoo”)."
      ),
      p(
        `Este formulario es tu consentimiento para ${workVerb} en el Estudio. En estos Términos, el “Trabajo” es ${work} que el ` +
          "Estudio te hace, tu “Registro” es todo lo que indicas en este formulario junto con el formulario que firmas, y la " +
          "“Copia Firmada” es el archivo PDF del formulario tal como lo firmaste. El Estudio hace el Trabajo y guarda el Registro."
      ),
      p("Aceptas estos Términos cuando marcas la casilla de este formulario que acepta los términos y condiciones."),
    ],
    sections: [
      {
        title: "Tus declaraciones",
        paragraphs: [
          clause(
            "a",
            "Declaras ante el Estudio que todo lo que ingresas en este formulario es verdadero y completo, incluidos quién eres, " +
              "cómo contactarte, tu edad y tus respuestas sobre tu salud. El Estudio confía en esas respuestas para cuidar tu " +
              "seguridad. Si algo falta o está mal, el riesgo es tuyo, y el Estudio no es responsable de lo que resulte de ello."
          ),
          clause(
            "b",
            "También declaras que no estás bajo los efectos del alcohol ni de drogas, que le has informado al Estudio sobre " +
              "cualquier condición que importe (como un embarazo, anticoagulantes, una afección de la piel, una enfermedad " +
              `contagiosa o una alergia que conozcas), y que entiendes que ${work} ${permanence}. Nadie en el Estudio te ha ` +
              "prometido que el Trabajo se pueda cambiar o quitar después."
          ),
        ],
      },
      {
        title: "Asunción de riesgos y exención de responsabilidad",
        paragraphs: [
          clause(
            "a",
            `Asunción de riesgos. Aceptas los riesgos de ${workVerb}: infección, reacción alérgica, cicatrices${jewelry}, y un ` +
              "resultado distinto del que imaginabas."
          ),
          clause(
            "b",
            "Exención. Liberas al Estudio, a sus artistas y a su personal de reclamaciones por lesiones, pérdidas o daños " +
              "derivados del Trabajo, salvo donde la ley no permita una exención."
          ),
          clause(
            "c",
            "Cuidados posteriores. Seguirás las instrucciones de cuidado posterior que el Estudio te dé, y entiendes que no " +
              "seguirlas es la causa más común de un mal resultado."
          ),
          clause(
            "d",
            "Indemnización. Mantendrás indemne al Estudio frente a reclamaciones que surjan de tu propio incumplimiento de estos " +
              "Términos."
          ),
          clause(
            "e",
            "Negativa del servicio. El Estudio puede negarse a hacer el Trabajo o detenerlo en cualquier momento, por cualquier " +
              "motivo que considere justo, y establece las normas de conducta dentro de su espacio."
          ),
        ],
      },
      {
        title: "Tu información",
        paragraphs: [
          clause(
            "a",
            "Recopilación. Este formulario recoge tu nombre, dirección, número de teléfono, correo electrónico, edad y fecha de " +
              "nacimiento; una fotografía de tu identificación oficial, tomada con tu teléfono o, donde el Estudio lo permite, " +
              "elegida de tus fotos guardadas; qué identificación es, cuando lo dices; tus respuestas a las preguntas de este " +
              "formulario, entre ellas las de tu salud, " +
              `que dependen del Trabajo y de la ley del lugar en que opera el Estudio; ${record}${jewelryKept}; cada casilla que ` +
              "marcas; el idioma en que firmas; y tu firma, con la fecha y la hora. Donde el Estudio o esa ley lo piden, este " +
              "formulario también recoge un contacto de emergencia, tu médico, el precio del Trabajo, cómo pagas y el número de tu " +
              "identificación; para un " +
              "menor, el nombre, el parentesco, el teléfono y la firma del padre, madre o tutor, el nombre y la firma del otro padre o madre o " +
              "una fotografía de su permiso por escrito, una fotografía de la identificación del menor y fotografías de los " +
              "documentos que pide esa ley, como un acta de nacimiento o los documentos de tutela; fotografías de una receta " +
              "médica o de las instrucciones escritas de un médico; fotografías de los números de lote de la aguja y de la " +
              "tinta; y tus respuestas a las cláusulas propias del Estudio, a sus preguntas propias y a su aviso de un cargo por " +
              "pagar con tarjeta. " +
              "Después de que firmas, donde el Estudio o esa ley lo piden, el artista añade su propia firma y los números de " +
              "lote y las fechas de vencimiento de los materiales que usó."
          ),
          clause(
            "b",
            "Conservación del Registro. Tu Registro se convierte en la Copia Firmada, que va al Estudio y queda archivada para el " +
              "Estudio, porque la ley del lugar donde opera el Estudio le exige conservarla, en la mayoría de los estados durante " +
              "al menos 2 años."
          ),
          clause(
            "c",
            "Almacenamiento. waivers.tattoo guarda el Registro para el Estudio, en servidores en Estados Unidos, cifrado, y no se " +
              "lo muestra a nadie más que al Estudio, y a las personas que operan waivers.tattoo y a los asistentes de " +
              "inteligencia artificial que usan, para ayudar al Estudio y para mantener waivers.tattoo funcionando."
          ),
          clause(
            "d",
            "Lo que sale de este formulario. Esta página no usa píxeles publicitarios ni scripts de terceros, y cuenta sus propias " +
              "cargas de página, los pasos de una firma y sus errores, sin nombre y sin cookie: cada carga de página lleva una " +
              "clave aleatoria hecha para esa carga de página, que tu navegador no guarda una vez cerrada la página. Lo que " +
              "escribes va al Estudio y a ningún otro lugar en el momento de la firma, con una excepción: salvo que el Estudio " +
              "esté en modo HIPAA, mientras escribes tu dirección, lo que llevas escrito va a Google para sugerirte la dirección " +
              "completa, y así puedes tocarla en lugar de escribir el resto. Nada " +
              "más de este formulario va a Google. Las sugerencias son contenido de Google Maps, y su uso se rige por las " +
              "versiones vigentes de los ",
            { text: "Términos del Servicio Adicionales de Google Maps para Usuarios Finales", href: GOOGLE_MAPS_TERMS_URL },
            " y de la ",
            { text: "Política de Privacidad de Google", href: GOOGLE_PRIVACY_URL },
            "."
          ),
          clause(
            "e",
            "Respuestas de salud e identificación. Tus respuestas sobre tu salud y la fotografía de tu identificación son la " +
              "información más sensible de este formulario. Se guardan en tu Registro y en la Copia Firmada, que va al Estudio y, " +
              "donde la ley del lugar en que opera el Estudio o el Estudio lo prevén, a ti, y el Estudio puede incluir tus " +
              "respuestas sobre tu salud en una exportación de sus propios registros; salvo que el Estudio esté en modo HIPAA, " +
              "tu navegador también guarda tus respuestas sobre tu salud hasta que firmes, como describe la Sección 3(f); y no " +
              "se guardan en ningún otro lugar: nunca en las palabras de un mensaje, nunca en publicidad y nunca compartidas " +
              "más allá del Estudio."
          ),
          clause(
            "f",
            "Lo que guarda tu navegador. Hasta que firmes, tu navegador guarda las respuestas que has escrito y las casillas " +
              "que has marcado en este formulario, en su propio almacenamiento para esta pestaña, para que recargar la página no " +
              "las pierda, y el formulario las vuelve a poner hasta 30 minutos después de lo último que escribiste. No guarda " +
              "ninguna fotografía, ninguna firma, ningún número de identificación ni ninguna de las 2 casillas sobre los " +
              "mensajes del Estudio; nada de lo que guarda sale de tu dispositivo; y firmar, o tocar “Empezar de nuevo”, lo borra. " +
              "Donde el Estudio está en modo HIPAA, tu navegador no guarda ninguna de tus respuestas sobre tu salud, ni tus " +
              "respuestas a las preguntas propias del Estudio, ni los datos de tu médico."
          ),
        ],
      },
      {
        title: "Mensajes del Estudio",
        paragraphs: [
          clause(
            "a",
            "Al firmar, permites que el Estudio te contacte sobre lo de hoy al teléfono y al correo electrónico que indicas: una " +
              "copia de tu formulario, tus instrucciones de cuidado posterior y lo relacionado con esta cita."
          ),
          clause(
            "b",
            "El consentimiento para recibir mensajes del Estudio después de hoy se da en 2 casillas aparte en este formulario, y " +
              "ambas empiezan sin marcar, porque son 2 cosas distintas que aceptar. La primera es sobre tu propia cicatrización: " +
              "el Estudio puede enviarte textos o correos sobre tus cuidados posteriores y cómo va sanando. La segunda es sobre " +
              "las novedades, ofertas y eventos del Estudio. Puedes marcar una, las dos o ninguna."
          ),
          clause(
            "c",
            "Tu número de móvil y estos consentimientos nunca se comparten con nadie con fines de marketing o promoción. Si dejas " +
              "ambas casillas sin marcar, nada de eso ocurre: aceptar los mensajes no es una condición para recibir el servicio, " +
              "puedes decir que no y firmar igual, y puedes darte de baja cuando quieras. Tus respuestas sobre tu salud y la " +
              "fotografía de tu identificación nunca forman parte de esto. Ni el Estudio ni waivers.tattoo venden tu información."
          ),
          clause(
            "d",
            "Los textos se envían a través de waivers.tattoo en nombre del Estudio; la frecuencia depende del Estudio, y pueden " +
              "aplicarse tarifas por mensajes y datos. Para dejar de recibir mensajes, responde STOP a un texto, usa el enlace de " +
              "un correo o díselo al Estudio; responde HELP a un texto para obtener ayuda. Para escribirle directamente a " +
              `waivers.tattoo, escribe a ${OPERATOR_EMAIL}.`
          ),
        ],
      },
      {
        title: "Edad",
        paragraphs: [
          // TW-0327 (2026-10-17): section 5 as the English now says it, written by the builder of the privacy policy's sixth
          // slice and awaiting a native-speaker read, like the rest of this page's Spanish, and never sent to Chris to read
          p(
            [
              minorWork
                ? `Donde la ley del lugar en que opera el Estudio permita que un menor se haga ${minorWork} con el consentimiento ` +
                  "de un padre, madre o tutor, y el Estudio lo permita, el padre, madre o tutor da ese consentimiento en este " +
                  "formulario y firma por el menor, y el Estudio es responsable de ese consentimiento y de su registro."
                : "",
              cosmetic
                ? "Este formulario no admite la firma de un menor para un tatuaje cosmético, ni siquiera con el consentimiento de " +
                  "un padre, madre o tutor."
                : "",
            ]
              .filter(Boolean)
              .join(" ")
          ),
        ],
      },
      {
        title: "Disposiciones generales",
        paragraphs: [
          clause(
            "a",
            "Ley aplicable. Tu acuerdo con el Estudio se rige por la ley del estado donde opera el Estudio, y cualquier disputa " +
              "sobre el Trabajo debe presentarse ante los tribunales de ese estado."
          ),
          clause("b", "Preguntas. Tus preguntas sobre tu Registro van primero al Estudio, porque el Registro es del Estudio."),
          clause(
            "c",
            "Privacidad. La Política de privacidad de waivers.tattoo, que explica cómo maneja waivers.tattoo lo que los estudios " +
              "recogen, está en ",
            privacyLink(),
            "."
          ),
          clause(
            "d",
            "Divisibilidad. Si alguna disposición de estos Términos se considera no exigible, las demás disposiciones siguen " +
              "vigentes."
          ),
          clause(
            "e",
            "Encabezados. Los encabezados de estos Términos son solo para facilitar la lectura y no afectan su significado."
          ),
        ],
      },
    ],
  };
}
